import { Chip, TextField } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import React, { useRef, useState } from 'react';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    id?: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    selectedItems: string[];
    inputRef?: any;
    icon?: any;
    clickable?: boolean;
    onChange: (items: string[]) => void;
    onClick?: (item: string) => void;
}

type Props<T> = IProps &
    Omit<AutocompleteProps<T, boolean, boolean, boolean>,
        "value" | "options" | "clickable" | "renderInput" | "onClick" | "onChange" | "loading" | "isOptionEqualToValue" | "renderTags" | "helperText" | "multiple" | "disabled">;

////////////////////////////////////////////////////////////////////////////

/**
 * Componente para incluir array de string em chip
 * @param props
 */
export const MprcChipInput: React.FC<Props<string>> = (props) => {

    const { selectedItems, label, placeholder, clickable, error, helperText, inputRef, icon, onClick, onChange,
        id = new Date().getTime().toString(),
        required = false,
        disabled = false,
    } = props;

    const [inputValue, setInputValue] = useState<string>("");
    const options = useRef<string[]>([]);

    ////////////////////////////////////////////////////////////////////////////
    /// HANDLES
    ////////////////////////////////////////////////////////////////////////////

    // Verifica se a tecla pressionada é o [Enter] e inclui o item
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode == 13 && inputValue.length > 0) {
            event.preventDefault();
            if (!selectedItems.map(x => x.toLocaleLowerCase()).includes(inputValue.toLocaleLowerCase())) {
                onChange([...selectedItems, inputValue]);
                setInputValue("");
            }
        }
    };

    //
    const handleAddItem = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode == 13 && inputValue.length > 0) {
            event.preventDefault();
        }
    };

    // Exclui o item pelo icone excluir do Chip
    const handleDeleteChip = (item: string | null) => {
        if (selectedItems && item) {
            const items = selectedItems.filter(x => x != item);
            onChange(items);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Autocomplete
            id={id}
            fullWidth
            disableClearable
            multiple
            disabled={disabled}
            openText=""
            open={false}
            options={options.current}
            clearOnEscape
            freeSolo
            inputValue={inputValue}
            value={selectedItems}
            isOptionEqualToValue={(option, value) => option === value}
            filterSelectedOptions={false}
            filterOptions={(options) => options}
            renderInput={(params) => (
                <TextField
                    error={error}
                    helperText={helperText}
                    {...params}
                    variant="standard"
                    label={label}
                    placeholder={placeholder}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        handleKeyDown(e);
                        handleAddItem(e);
                    }}
                    required={required}
                    fullWidth
                    slotProps={{
                        input: {
                            ...params.InputProps,    
                        }
                    }}
                    inputRef={inputRef}
                />
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    if (option) {
                        return (
                            <Chip
                                icon={icon}
                                label={option}
                                clickable={clickable}
                                onClick={() => onClick && onClick(option)}
                                {...getTagProps({ index })}
                                size="small"
                                onDelete={() => handleDeleteChip(option)}
                            />);
                    }
                    return null;
                })
            }

        />
    );
};
